import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import StatusBadge from './StatusBadge';
import { getNotificationDisplay, openNotificationTarget, NotificationChangeList } from '../utils/notificationDetails';
import {
  Users, Building2, Server, Smartphone, Monitor, Printer, AlertTriangle,
  CheckCircle2, Clock, Wrench, RefreshCw, PlusCircle, Search, ShieldAlert,
  ArrowRight, Activity, PackageCheck, Zap, HardDrive, BellRing, Layers
} from 'lucide-react';
import AnnouncementNotifications from './AnnouncementNotifications';

interface SummaryData {
  stats: {
    users: number;
    companies: number;
    computers: { total: number; assigned: number; in_stock: number; maintenance: number };
    mobilePhones: { total: number; assigned: number; in_stock: number; maintenance: number };
    monitors: { total: number; assigned: number; in_stock: number };
    printers: number;
    physicalServers: number;
    virtualServers: number;
    technicalServices: { total: number; active: number };
  };
  attentionNeeded: {
    pendingApprovalsCount: number;
    pendingApprovals: any[];
    lowStockConsumablesCount: number;
    lowStockConsumables: any[];
    expiringWarrantiesCount: number;
    expiringWarranties: any[];
    activeServicesCount: number;
  };
  activities: any[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [systemPerformance, setSystemPerformance] = useState({
    cpu: { percentage: 0 },
    memory: { percentage: 0 },
    disk: { percentage: 0 },
    network: { percentage: 0 }
  });

  useEffect(() => {
    loadSummaryData();
    const interval = setInterval(async () => {
      try {
        const perf = await apiService.getSystemPerformance();
        setSystemPerformance(perf);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSummaryData = async () => {
    try {
      setLoading(true);
      const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSummaryData(data);
      }

      const perf = await apiService.getSystemPerformance().catch(() => ({
        cpu: { percentage: 0 },
        memory: { percentage: 0 },
        disk: { percentage: 0 },
        network: { percentage: 0 }
      }));
      setSystemPerformance(perf);
    } catch (error) {
      console.error('Error loading dashboard summary:', error);
      showGlobalNotification.error(t('common.error'), 'Dashboard verileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSummaryData();
    setRefreshing(false);
    showGlobalNotification.success('Başarılı', 'Dashboard verileri güncellendi.');
  };

  const navigateTo = (page: string) => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  };

  if (loading && !summaryData) {
    return (
      <div className="p-6 min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Dashboard hazırlanıyor...</p>
        </div>
      </div>
    );
  }

  const stats = summaryData?.stats;
  const attention = summaryData?.attentionNeeded;
  const activities = summaryData?.activities || [];

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      
      {/* 🚀 Top Header & Quick Action Launch Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              IT Toolbox Komuta Merkezi
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              v1.0.0 Live
            </span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Hoş geldiniz, <strong className="text-gray-800 dark:text-gray-200">{user?.name}</strong>. İşte sistemin anlık görünümü ve aksiyon bekleyen süreçler.
          </p>
        </div>

        {/* Quick Launch Buttons */}
        <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto">
          <button
            onClick={() => navigateTo('assignment-forms')}
            className="flex-1 lg:flex-none inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-indigo-200 dark:shadow-none"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Yeni Zimmet
          </button>
          
          <button
            onClick={() => navigateTo('technical-service')}
            className="flex-1 lg:flex-none inline-flex items-center justify-center px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-amber-200 dark:shadow-none"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Arıza Kaydı Aç
          </button>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openGlobalSearch'))}
            className="inline-flex items-center justify-center px-3.5 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-xl transition-colors cursor-pointer"
            title="Varlık / IMEI Ara"
          >
            <Search className="w-4 h-4 mr-1.5" />
            Varlık Ara
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl transition-colors disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Announcements */}
      {(user?.role === 'admin' || user?.role === 'superadmin') && (
        <AnnouncementNotifications />
      )}

      {/* 🚨 Eylem Gerektiren İşler (Attention Needed Center) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BellRing className="w-5 h-5 text-rose-500 animate-bounce" />
            Eylem Gerektiren Süreçler
          </h2>
          <span className="text-xs text-gray-500 font-medium">Anlık Takip Paneli</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* 1. Bekleyen Zimmet Onayları */}
          <div 
            onClick={() => navigateTo('assignment-forms')}
            className={`p-4 rounded-2xl border transition-all cursor-pointer ${
              (attention?.pendingApprovalsCount || 0) > 0 
                ? 'bg-amber-50/80 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 hover:shadow-md hover:border-amber-400' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-80'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                Dijital Zimmet Onayları
              </span>
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-amber-900 dark:text-amber-100">
                {attention?.pendingApprovalsCount || 0}
              </span>
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">Onay Bekliyor</span>
            </div>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-2 flex items-center justify-between">
              <span>Kullanıcılara e-posta gönderildi</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </p>
          </div>

          {/* 2. Stok Alarmı Veren Sarf Malzemeleri */}
          <div 
            onClick={() => navigateTo('consumables')}
            className={`p-4 rounded-2xl border transition-all cursor-pointer ${
              (attention?.lowStockConsumablesCount || 0) > 0 
                ? 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 hover:shadow-md hover:border-rose-400' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-80'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-rose-800 dark:text-rose-300">
                Kritik Sarf Stokları
              </span>
              <AlertTriangle className="w-4 h-4 text-rose-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-rose-900 dark:text-rose-100">
                {attention?.lowStockConsumablesCount || 0}
              </span>
              <span className="text-xs text-rose-700 dark:text-rose-400 font-medium">Stok Sınırında</span>
            </div>
            <p className="text-xs text-rose-700/80 dark:text-rose-400/80 mt-2 flex items-center justify-between">
              <span>Toner & Çevre birimleri azaldı</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </p>
          </div>

          {/* 3. Aktif Arıza & Servis Süreçleri */}
          <div 
            onClick={() => navigateTo('technical-service')}
            className={`p-4 rounded-2xl border transition-all cursor-pointer ${
              (attention?.activeServicesCount || 0) > 0 
                ? 'bg-indigo-50/80 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800 hover:shadow-md hover:border-indigo-400' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-80'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-300">
                Aktif Arıza & Servis
              </span>
              <Wrench className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-indigo-900 dark:text-indigo-100">
                {attention?.activeServicesCount || 0}
              </span>
              <span className="text-xs text-indigo-700 dark:text-indigo-400 font-medium">İşlemde</span>
            </div>
            <p className="text-xs text-indigo-700/80 dark:text-indigo-400/80 mt-2 flex items-center justify-between">
              <span>Tedarikçide / Tamirde olanlar</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </p>
          </div>

          {/* 4. Garantisi Yaklaşan Cihazlar */}
          <div 
            onClick={() => navigateTo('computers')}
            className={`p-4 rounded-2xl border transition-all cursor-pointer ${
              (attention?.expiringWarrantiesCount || 0) > 0 
                ? 'bg-amber-50/80 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 hover:shadow-md hover:border-amber-400' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-80'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                Garanti Bitişleri (30 Gün)
              </span>
              <ShieldAlert className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-amber-900 dark:text-amber-100">
                {attention?.expiringWarrantiesCount || 0}
              </span>
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">Cihaz / Sözleşme</span>
            </div>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-2 flex items-center justify-between">
              <span>Garanti süresi dolmak üzere</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </p>
          </div>

        </div>
      </div>

      {/* 📊 Ana Varlık Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        
        {/* Bilgisayarlar */}
        <div 
          onClick={() => navigateTo('computers')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Bilgisayarlar</span>
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 rounded-xl text-indigo-600">
              <HardDrive className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {stats?.computers.total || 0}
          </div>
          <div className="mt-2 text-xs flex justify-between text-gray-500">
            <span>Zimmetli: <b className="text-indigo-600">{stats?.computers.assigned || 0}</b></span>
            <span>Stok: <b className="text-emerald-600">{stats?.computers.in_stock || 0}</b></span>
          </div>
        </div>

        {/* Akıllı Telefonlar */}
        <div 
          onClick={() => navigateTo('mobile-phones')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Telefonlar</span>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/40 rounded-xl text-emerald-600">
              <Smartphone className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {stats?.mobilePhones.total || 0}
          </div>
          <div className="mt-2 text-xs flex justify-between text-gray-500">
            <span>Zimmetli: <b className="text-indigo-600">{stats?.mobilePhones.assigned || 0}</b></span>
            <span>Stok: <b className="text-emerald-600">{stats?.mobilePhones.in_stock || 0}</b></span>
          </div>
        </div>

        {/* Monitörler */}
        <div 
          onClick={() => navigateTo('monitors')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Monitörler</span>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-xl text-blue-600">
              <Monitor className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {stats?.monitors.total || 0}
          </div>
          <div className="mt-2 text-xs flex justify-between text-gray-500">
            <span>Zimmetli: <b className="text-indigo-600">{stats?.monitors.assigned || 0}</b></span>
            <span>Stok: <b className="text-emerald-600">{stats?.monitors.in_stock || 0}</b></span>
          </div>
        </div>

        {/* Yazıcılar */}
        <div 
          onClick={() => navigateTo('printers')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Yazıcılar</span>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/40 rounded-xl text-amber-600">
              <Printer className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {stats?.printers || 0}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Sistemdeki toplam yazıcılar
          </div>
        </div>

        {/* Fiziksel / Sanal Sunucular */}
        <div 
          onClick={() => navigateTo('physical-servers')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Sunucular</span>
            <div className="p-2 bg-purple-50 dark:bg-purple-900/40 rounded-xl text-purple-600">
              <Server className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {(stats?.physicalServers || 0) + (stats?.virtualServers || 0)}
          </div>
          <div className="mt-2 text-xs flex justify-between text-gray-500">
            <span>Fiziksel: <b>{stats?.physicalServers || 0}</b></span>
            <span>Sanal: <b>{stats?.virtualServers || 0}</b></span>
          </div>
        </div>

        {/* Kullanıcılar */}
        <div 
          onClick={() => navigateTo('users')}
          className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold">Kullanıcılar</span>
            <div className="p-2 bg-pink-50 dark:bg-pink-900/40 rounded-xl text-pink-600">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">
            {stats?.users || 0}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Aktif personel & IT hesapları
          </div>
        </div>

      </div>

      {/* 🟢 Görsel Varlık Dağılımı ve Canlı Aktivite Akışı */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column (2 cols): Varlık Dağılım Oranları */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Cihaz Durum Dağılımları */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-600" />
              Zimmet & Stok Dağılım Oranları
            </h3>

            <div className="space-y-4">
              {/* Bilgisayarlar Bar */}
              <div>
                <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                  <span>Bilgisayarlar ({stats?.computers.total || 0} Adet)</span>
                  <span>%{stats?.computers.total ? Math.round(((stats.computers.assigned) / stats.computers.total) * 100) : 0} Zimmette</span>
                </div>
                <div className="h-3 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                  <div 
                    style={{ width: `${stats?.computers.total ? (stats.computers.assigned / stats.computers.total) * 100 : 0}%` }}
                    className="bg-indigo-600 h-full" 
                    title={`Zimmetli: ${stats?.computers.assigned}`}
                  />
                  <div 
                    style={{ width: `${stats?.computers.total ? (stats.computers.in_stock / stats.computers.total) * 100 : 0}%` }}
                    className="bg-emerald-500 h-full" 
                    title={`Stokta: ${stats?.computers.in_stock}`}
                  />
                  <div 
                    style={{ width: `${stats?.computers.total ? (stats.computers.maintenance / stats.computers.total) * 100 : 0}%` }}
                    className="bg-amber-500 h-full" 
                    title={`Serviste: ${stats?.computers.maintenance}`}
                  />
                </div>
              </div>

              {/* Akıllı Telefonlar Bar */}
              <div>
                <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                  <span>Telefonlar ({stats?.mobilePhones.total || 0} Adet)</span>
                  <span>%{stats?.mobilePhones.total ? Math.round(((stats.mobilePhones.assigned) / stats.mobilePhones.total) * 100) : 0} Zimmette</span>
                </div>
                <div className="h-3 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                  <div 
                    style={{ width: `${stats?.mobilePhones.total ? (stats.mobilePhones.assigned / stats.mobilePhones.total) * 100 : 0}%` }}
                    className="bg-indigo-600 h-full" 
                    title={`Zimmetli: ${stats?.mobilePhones.assigned}`}
                  />
                  <div 
                    style={{ width: `${stats?.mobilePhones.total ? (stats.mobilePhones.in_stock / stats.mobilePhones.total) * 100 : 0}%` }}
                    className="bg-emerald-500 h-full" 
                    title={`Stokta: ${stats?.mobilePhones.in_stock}`}
                  />
                  <div 
                    style={{ width: `${stats?.mobilePhones.total ? (stats.mobilePhones.maintenance / stats.mobilePhones.total) * 100 : 0}%` }}
                    className="bg-amber-500 h-full" 
                    title={`Serviste: ${stats?.mobilePhones.maintenance}`}
                  />
                </div>
              </div>

              {/* Legend */}
              <div className="pt-2 flex items-center justify-end gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span> Zimmetli</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Stokta / Boşta</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> Servis / Bakım</span>
              </div>
            </div>
          </div>

          {/* Sistem Performans Paneli */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Sunucu Donanım Sağlığı (Real-Time)
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <span className="text-xs text-gray-500">CPU Yükü</span>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                  %{systemPerformance.cpu?.percentage || 0}
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <span className="text-xs text-gray-500">RAM Kullanımı</span>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                  %{systemPerformance.memory?.percentage || 0}
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <span className="text-xs text-gray-500">Disk Doluluğu</span>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                  %{systemPerformance.disk?.percentage || 0}
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <span className="text-xs text-gray-500">Ağ Trafiği</span>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                  %{systemPerformance.network?.percentage || 0}
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Right Column (1 col): Live Activity Stream */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" />
              {t('dashboard.recentActivities')}
            </h3>
            <span className="text-xs text-gray-400 font-medium">{t('dashboard.liveFeed')}</span>
          </div>

          <div className="flex-1 overflow-y-auto max-h-[380px] space-y-3 pr-1">
            {activities.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">{t('dashboard.noActivities')}</p>
            ) : (
              activities.map((act: any, idx: number) => {
                const display = getNotificationDisplay(act, t, language);
                const createdAt = new Date(act.created_at || Date.now());
                const locale = language === 'en' ? 'en-US' : 'tr-TR';
                const isToday = createdAt.toDateString() === new Date().toDateString();
                const clickable = !!act.action_url;
                return (
                <div
                  key={act.id || idx}
                  onClick={clickable ? () => openNotificationTarget(act) : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNotificationTarget(act); } } : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  title={clickable ? t('notif.change.open') : undefined}
                  className={`p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40 text-xs border border-gray-100 dark:border-gray-700/50 ${
                    clickable ? 'cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors' : ''
                  }`}
                >
                  <div className="flex items-center justify-between font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    <span>{display.title}</span>
                    <StatusBadge status={act.type === 'error' ? 'rejected' : act.type === 'success' ? 'approved' : 'pending'} size="sm" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 line-clamp-2">{display.message}</p>
                  <NotificationChangeList changes={display.changes} t={t} maxItems={3} className="mt-1" />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    {isToday
                      ? createdAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
                      : createdAt.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                );
              })
            )}
          </div>
        </div>

      </div>

    </div>
  );
};

export default Dashboard;
